import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import { useContentTab } from "@/components/brain/ContentTabContext";
import { useSidebarState } from "../../../../hooks/useSidebarState";
import { ChevronDoubleLeftIcon, ArrowLeftIcon, MagnifyingGlassIcon, ChatBubbleLeftIcon, Squares2X2Icon, ShareIcon, LinkIcon, CheckIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import WavePicture from "../../../waves/WavePicture";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { createBreakpoint } from "react-use";
import WaveDropsSearchModal from "@/components/waves/drops/search/WaveDropsSearchModal";
import { MyStreamWaveTab } from "@/types/waves.types";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveShareCopyAction } from "@/hooks/waves/useWaveShareCopyAction";
import WaveDescriptionPopover from "@/components/waves/header/WaveDescriptionPopover";
import { getWaveDescriptionPreviewText } from "@/helpers/waves/waveDescriptionPreview";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import MyStreamActionTooltip from "../MyStreamActionTooltip";
import type { ChatSubmitDropAction } from "../chatSubmitDrop.types";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
interface MyStreamWaveTabsDefaultProps {
  readonly wave: ApiWave;
  readonly viewMode: WaveViewMode;
  readonly onToggleViewMode: () => void;
  readonly showGalleryToggle: boolean;
  readonly activeCurationId?: string | null | undefined;
  readonly onSelectCuration?: ((curationId: string | null) => void) | undefined;
  readonly chatSubmitDropAction?: ChatSubmitDropAction | null | undefined;
}
const MyStreamWaveTabsDefault: React.FC<MyStreamWaveTabsDefaultProps> = t0 => {
  const $ = _c(99);
  const {
    wave,
    viewMode,
    onToggleViewMode,
    showGalleryToggle,
    activeCurationId: t1,
    onSelectCuration: t2,
    chatSubmitDropAction: t3
  } = t0;
  const activeCurationId = t1 === undefined ? null : t1;
  const onSelectCuration = t2 === undefined ? _temp : t2;
  const chatSubmitDropAction = t3 === undefined ? null : t3;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const {
    toggleRightSidebar,
    isRightSidebarOpen
  } = useSidebarState();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const breakpoint = useBreakpoint();
  const showBackButton = breakpoint !== "LG";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const waveChatScroll = useWaveChatScrollOptional();
  const isDirectMessage = wave.chat.scope.group?.is_direct_message ?? false;
  const showShareAction = !isDirectMessage;
  let t4;
  if ($[0] !== wave) {
    t4 = getWaveDescriptionPreviewText(wave);
    $[0] = wave;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const previewText = t4;
  const showDescriptionPreview = showShareAction && !!previewText;
  let t5;
  if ($[2] !== isDirectMessage || $[3] !== wave.id || $[4] !== wave.name) {
    t5 = {
      waveId: wave.id,
      waveName: wave.name,
      isDirectMessage
    };
    $[2] = isDirectMessage;
    $[3] = wave.id;
    $[4] = wave.name;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    mode: waveLinkActionMode,
    label: waveLinkActionLabel,
    feedbackState: waveLinkActionFeedbackState,
    onClick: handleWaveLinkActionClick
  } = useWaveShareCopyAction(t5);
  let t6;
  if ($[6] !== isApp || $[7] !== router || $[8] !== searchParams || $[9] !== wave.chat.scope.group?.is_direct_message) {
    t6 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      params.delete("serialNo");
      params.delete("divider");
      params.delete("drop");
      params.delete("curation");
      const basePath = getWaveHomeRoute({
        isDirectMessage: wave.chat.scope.group?.is_direct_message ?? false,
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;
      router.push(newUrl, {
        scroll: false
      });
    };
    $[6] = isApp;
    $[7] = router;
    $[8] = searchParams;
    $[9] = wave.chat.scope.group?.is_direct_message;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleMobileBack = t6;
  let t7;
  if ($[11] !== onSelectCuration || $[12] !== pathname || $[13] !== router || $[14] !== searchParams || $[15] !== setActiveContentTab || $[16] !== wave.id || $[17] !== waveChatScroll) {
    t7 = serialNo => {
      onSelectCuration(null);
      setActiveContentTab(MyStreamWaveTab.CHAT);
      if (waveChatScroll) {
        waveChatScroll.requestScrollToSerialNo({
          waveId: wave.id,
          serialNo
        });
        return;
      }
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.delete("curation");
      params_0.set("serialNo", String(serialNo));
      router.replace(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[11] = onSelectCuration;
    $[12] = pathname;
    $[13] = router;
    $[14] = searchParams;
    $[15] = setActiveContentTab;
    $[16] = wave.id;
    $[17] = waveChatScroll;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleSearchSelect = t7;
  const waveLinkActionIconColor = waveLinkActionFeedbackState === "idle" ? "tw-text-iron-200" : "tw-text-emerald-300";
  const headerActionsTooltipId = `my-stream-wave-header-actions-${wave.id}`;
  const galleryToggleLabel = viewMode === "chat" ? "Switch to gallery view" : "Switch to chat view";
  const rightSidebarActionLabel = isRightSidebarOpen ? "Hide right sidebar" : "Show right sidebar";
  const showChatSubmitDropAction = activeContentTab === MyStreamWaveTab.CHAT && !activeCurationId && chatSubmitDropAction?.isVisible === true;
  const chatSubmitDropTooltip = chatSubmitDropAction ? chatSubmitDropAction.restrictionMessage ?? chatSubmitDropAction.label : null;
  const chatSubmitDropTitle = chatSubmitDropTooltip ?? undefined;
  const chatSubmitDropAriaLabel = chatSubmitDropAction ? chatSubmitDropAction.label : undefined;
  let t8;
  if ($[19] !== waveLinkActionFeedbackState || $[20] !== waveLinkActionMode) {
    t8 = () => {
      if (waveLinkActionFeedbackState !== "idle") {
        return <CheckIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      if (waveLinkActionMode === "share") {
        return <ShareIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
      }
      return <LinkIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    };
    $[19] = waveLinkActionFeedbackState;
    $[20] = waveLinkActionMode;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const renderWaveLinkActionIcon = t8;
  let t9;
  if ($[22] !== handleMobileBack || $[23] !== showBackButton) {
    t9 = showBackButton && <button onClick={handleMobileBack} className="-tw-ml-2.5 tw-mr-1.5 tw-flex tw-h-full tw-items-center tw-border-0 tw-bg-transparent tw-p-0 tw-px-2.5 tw-text-iron-300 tw-transition-colors hover:tw-text-iron-50" aria-label="Go back"><ArrowLeftIcon className="tw-h-6 tw-w-6 tw-flex-shrink-0" /></button>;
    $[22] = handleMobileBack;
    $[23] = showBackButton;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  const t10 = wave.name;
  const t11 = wave.picture;
  let t12;
  if ($[25] !== wave.contributors_overview) {
    t12 = wave.contributors_overview.map(_temp2);
    $[25] = wave.contributors_overview;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== wave.name || $[29] !== wave.picture) {
    t13 = <div className="tw-size-9 tw-flex-shrink-0 tw-rounded-full tw-ring-1 tw-ring-white/30 tw-ring-offset-1 tw-ring-offset-iron-950"><WavePicture name={t10} picture={t11} contributors={t12} /></div>;
    $[27] = t12;
    $[28] = wave.name;
    $[29] = wave.picture;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== previewText || $[32] !== showDescriptionPreview || $[33] !== wave) {
    t14 = showDescriptionPreview ? <WaveDescriptionPopover wave={wave} align="left" ariaLabel="Show wave description" triggerClassName="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-left"><h1 className="tw-mb-0 tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1><span className="tw-mt-0.5 tw-block tw-w-full tw-truncate tw-text-xs tw-font-normal tw-text-iron-400">{previewText}</span></WaveDescriptionPopover> : <h1 className="tw-mb-0 tw-truncate tw-text-sm tw-font-semibold tw-tracking-tight tw-text-white/95 lg:tw-text-xl">{wave.name}</h1>;
    $[31] = previewText;
    $[32] = showDescriptionPreview;
    $[33] = wave;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14 || $[37] !== t9) {
    t15 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-x-3">{t9}{t13}{t14}</div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t9;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== chatSubmitDropAction || $[40] !== chatSubmitDropAriaLabel || $[41] !== chatSubmitDropTitle || $[42] !== chatSubmitDropTooltip || $[43] !== headerActionsTooltipId || $[44] !== showChatSubmitDropAction) {
    t16 = showChatSubmitDropAction && <span className="tw-inline-flex" title={chatSubmitDropTitle} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={chatSubmitDropTooltip}><PrimaryButton loading={false} disabled={!chatSubmitDropAction.canOpen} onClicked={chatSubmitDropAction.onOpen} padding="tw-px-2.5 tw-py-2" title={chatSubmitDropTitle} ariaLabel={chatSubmitDropAriaLabel}><PlusIcon className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>{chatSubmitDropAction.compactLabel}</span></PrimaryButton></span>;
    $[39] = chatSubmitDropAction;
    $[40] = chatSubmitDropAriaLabel;
    $[41] = chatSubmitDropTitle;
    $[42] = chatSubmitDropTooltip;
    $[43] = headerActionsTooltipId;
    $[44] = showChatSubmitDropAction;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== activeCurationId || $[47] !== galleryToggleLabel || $[48] !== headerActionsTooltipId || $[49] !== onToggleViewMode || $[50] !== showGalleryToggle || $[51] !== viewMode) {
    t17 = showGalleryToggle && !activeCurationId && <button type="button" onClick={onToggleViewMode} aria-label={galleryToggleLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={galleryToggleLabel} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{viewMode === "chat" ? <Squares2X2Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ChatBubbleLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}</button>;
    $[46] = activeCurationId;
    $[47] = galleryToggleLabel;
    $[48] = headerActionsTooltipId;
    $[49] = onToggleViewMode;
    $[50] = showGalleryToggle;
    $[51] = viewMode;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== handleWaveLinkActionClick || $[54] !== headerActionsTooltipId || $[55] !== renderWaveLinkActionIcon || $[56] !== showShareAction || $[57] !== waveLinkActionIconColor || $[58] !== waveLinkActionLabel || $[59] !== waveLinkActionMode) {
    t18 = showShareAction && <button type="button" onClick={handleWaveLinkActionClick} aria-label={waveLinkActionLabel} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={waveLinkActionLabel} data-wave-link-action-mode={waveLinkActionMode} className={`tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white ${waveLinkActionIconColor}`}>{renderWaveLinkActionIcon()}</button>;
    $[53] = handleWaveLinkActionClick;
    $[54] = headerActionsTooltipId;
    $[55] = renderWaveLinkActionIcon;
    $[56] = showShareAction;
    $[57] = waveLinkActionIconColor;
    $[58] = waveLinkActionLabel;
    $[59] = waveLinkActionMode;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => setIsSearchOpen(true);
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <MagnifyingGlassIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />;
    $[62] = t20;
  } else {
    t20 = $[62];
  }
  let t21;
  if ($[63] !== headerActionsTooltipId) {
    t21 = <button type="button" onClick={t19} aria-label="Search messages in this wave" data-tooltip-id={headerActionsTooltipId} data-tooltip-content="Search messages in this wave" className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{t20}</button>;
    $[63] = headerActionsTooltipId;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  const t22 = `tw-h-4 tw-w-4 tw-flex-shrink-0 tw-text-iron-200 tw-transition tw-duration-300 ${isRightSidebarOpen ? "tw-rotate-180 desktop-hover:group-hover:tw-translate-x-0.5" : "tw-rotate-0 desktop-hover:group-hover:-tw-translate-x-0.5"}`;
  let t23;
  if ($[65] !== t22) {
    t23 = <ChevronDoubleLeftIcon strokeWidth={2} className={t22} />;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== headerActionsTooltipId || $[68] !== rightSidebarActionLabel || $[69] !== t23 || $[70] !== toggleRightSidebar) {
    t24 = <button type="button" onClick={toggleRightSidebar} data-tooltip-id={headerActionsTooltipId} data-tooltip-content={rightSidebarActionLabel} className="tw-group tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-800 tw-shadow-[0_12px_28px_rgba(0,0,0,0.35)] tw-backdrop-blur-sm tw-transition tw-duration-300 tw-ease-out desktop-hover:hover:tw-border-iron-500/80 desktop-hover:hover:tw-bg-iron-700/85 desktop-hover:hover:tw-shadow-[0_16px_34px_rgba(0,0,0,0.4)]" aria-label={rightSidebarActionLabel}>{t23}</button>;
    $[67] = headerActionsTooltipId;
    $[68] = rightSidebarActionLabel;
    $[69] = t23;
    $[70] = toggleRightSidebar;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== t16 || $[73] !== t17 || $[74] !== t18 || $[75] !== t21 || $[76] !== t24) {
    t25 = <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 tw-self-stretch">{t16}{t17}{t18}{t21}{t24}</div>;
    $[72] = t16;
    $[73] = t17;
    $[74] = t18;
    $[75] = t21;
    $[76] = t24;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== t15 || $[79] !== t25) {
    t26 = <div className="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-2 tw-py-3 sm:tw-px-4">{t15}{t25}</div>;
    $[78] = t15;
    $[79] = t25;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== headerActionsTooltipId) {
    t27 = <MyStreamActionTooltip id={headerActionsTooltipId} />;
    $[81] = headerActionsTooltipId;
    $[82] = t27;
  } else {
    t27 = $[82];
  }
  let t28;
  if ($[83] !== activeContentTab || $[84] !== activeCurationId || $[85] !== onSelectCuration || $[86] !== setActiveContentTab || $[87] !== wave) {
    t28 = <div className="tw-border-b tw-border-l-0 tw-border-t-0 tw-border-solid tw-border-iron-800"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} /></div>;
    $[83] = activeContentTab;
    $[84] = activeCurationId;
    $[85] = onSelectCuration;
    $[86] = setActiveContentTab;
    $[87] = wave;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = () => setIsSearchOpen(false);
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== handleSearchSelect || $[91] !== isSearchOpen || $[92] !== wave) {
    t30 = <WaveDropsSearchModal isOpen={isSearchOpen} onClose={t29} wave={wave} onSelectSerialNo={handleSearchSelect} />;
    $[90] = handleSearchSelect;
    $[91] = isSearchOpen;
    $[92] = wave;
    $[93] = t30;
  } else {
    t30 = $[93];
  }
  let t31;
  if ($[94] !== t26 || $[95] !== t27 || $[96] !== t28 || $[97] !== t30) {
    t31 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t26}{t27}{t28}{t30}</div>;
    $[94] = t26;
    $[95] = t27;
    $[96] = t28;
    $[97] = t30;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  return t31;
};
export default MyStreamWaveTabsDefault;
function _temp() {}
function _temp2(c) {
  return {
    pfp: c.contributor_pfp,
    identity: c.contributor_identity
  };
}